import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * StatusBadge — the one status pill for the whole portal.
 *
 * Before this component, every page hand-rolled its own status chip
 * (Payments, Invoices, Admin customer lists, returns, cutovers…), each with
 * slightly different padding, radius, weight and color. This unifies them:
 * one shape, one set of semantic tones, one place to adjust.
 *
 * Pick a semantic `tone` and pass a label as children:
 *   <StatusBadge tone="success">Paid</StatusBadge>
 *
 * Or, for the payment-status vocabulary that recurs across pages, use the
 * `paymentStatusBadge()` helper to map a raw status string to {tone,label}.
 */

export type BadgeTone = 'success' | 'info' | 'warning' | 'danger' | 'neutral' | 'brand';

const TONE: Record<BadgeTone, string> = {
  success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  info: 'bg-blue-50 text-blue-700 border-blue-200',
  warning: 'bg-amber-50 text-amber-800 border-amber-200',
  danger: 'bg-red-50 text-red-700 border-red-200',
  neutral: 'bg-slate-100 text-slate-600 border-slate-200',
  brand: 'bg-brix-primary-tint text-brix-primary border-brix-primary/30',
};

const DOT_TONE: Record<BadgeTone, string> = {
  success: 'bg-emerald-500',
  info: 'bg-blue-500',
  warning: 'bg-amber-500',
  danger: 'bg-red-500',
  neutral: 'bg-slate-400',
  brand: 'bg-brix-primary',
};

export interface StatusBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  /** Show a leading status dot. */
  dot?: boolean;
  /** Optional leading icon (lucide element). Ignored when `dot` is set. */
  icon?: React.ReactNode;
  size?: 'sm' | 'md';
}

export function StatusBadge({
  tone = 'neutral',
  dot = false,
  icon,
  size = 'md',
  className,
  children,
  ...props
}: StatusBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md border font-semibold tracking-tight whitespace-nowrap',
        size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs',
        TONE[tone],
        className,
      )}
      {...props}
    >
      {dot ? (
        <span className={cn('h-1.5 w-1.5 rounded-full', DOT_TONE[tone])} aria-hidden />
      ) : icon ? (
        <span className="shrink-0" aria-hidden>{icon}</span>
      ) : null}
      {children}
    </span>
  );
}

/**
 * Map an orders.payments status to a badge tone + human label. This is the
 * vocabulary shared by /payments, the admin payments console, and anywhere a
 * payment row is shown.
 */
export function paymentStatusBadge(status: string): { tone: BadgeTone; label: string } {
  switch (status) {
    case 'succeeded':
      return { tone: 'success', label: 'Paid' };
    case 'processing':
    case 'submitted':
      return { tone: 'info', label: 'Processing' };
    case 'pending':
      return { tone: 'warning', label: 'Pending' };
    case 'failed':
      return { tone: 'danger', label: 'Failed' };
    case 'returned':
      return { tone: 'danger', label: 'Returned' };
    case 'refunded':
      return { tone: 'neutral', label: 'Refunded' };
    default:
      return { tone: 'neutral', label: status };
  }
}
